import { 
  collection, 
  addDoc, 
  getDocs,
  query, 
  where, 
  orderBy,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore'
import { db } from '../firebase'
import config from '../config/environment'

// Development mode flag
const DEV_MODE = config.devMode

// No demo branches are shipped. For local dev, use Firestore + emulators.
const mockBranches = []

export class BranchService {
  
  /**
   * Get all branches
   */
  static async getAllBranches() {
    try {
      if (DEV_MODE) {
        return mockBranches
      }
      
      const snapshot = await getDocs(collection(db, 'branches'))
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (error) {
      console.error('Error fetching branches:', error)
      throw new Error('Failed to fetch branches')
    }
  }

  /**
   * Get branch by ID
   */
  static async getBranchById(branchId) {
    try {
      if (DEV_MODE) {
        return mockBranches.find(branch => branch.id === branchId)
      }
      
      const docRef = doc(db, 'branches', branchId)
      const docSnap = await getDoc(docRef)
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        }
      }
      
      return null
    } catch (error) {
      console.error('Error fetching branch:', error)
      throw new Error('Failed to fetch branch')
    }
  }

  /**
   * Create new branch
   */
  static async createBranch(branchData) {
    try {
      if (DEV_MODE) {
        const newBranch = {
          id: `branch-${Date.now()}`,
          ...branchData,
          createdAt: new Date(),
          createdBy: 'current-user'
        }
        mockBranches.push(newBranch)
        return newBranch.id
      }
      
      const docRef = await addDoc(collection(db, 'branches'), {
        ...branchData,
        createdAt: serverTimestamp(),
        createdBy: 'current-user' // TODO: Get actual user ID
      })
      
      return docRef.id
    } catch (error) {
      console.error('Error creating branch:', error)
      throw new Error('Failed to create branch')
    }
  }

  /**
   * Update branch
   */
  static async updateBranch(branchId, updateData) {
    try {
      if (DEV_MODE) {
        const branchIndex = mockBranches.findIndex(branch => branch.id === branchId)
        if (branchIndex !== -1) {
          mockBranches[branchIndex] = {
            ...mockBranches[branchIndex],
            ...updateData,
            updatedAt: new Date()
          }
        }
        return true
      }
      
      const docRef = doc(db, 'branches', branchId)
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      })
      
      return true
    } catch (error) {
      console.error('Error updating branch:', error)
      throw new Error('Failed to update branch')
    }
  }

  /**
   * Delete branch
   */
  static async deleteBranch(branchId) {
    try {
      if (DEV_MODE) {
        const branchIndex = mockBranches.findIndex(branch => branch.id === branchId)
        if (branchIndex !== -1) {
          mockBranches.splice(branchIndex, 1)
        }
        return true
      }
      
      const docRef = doc(db, 'branches', branchId)
      await deleteDoc(docRef)
      
      return true
    } catch (error) {
      console.error('Error deleting branch:', error)
      throw new Error('Failed to delete branch')
    }
  }

  /**
   * Get branch statistics
   */
  static async getBranchStats(branchId, dateRange = null) {
    try {
      if (DEV_MODE) {
        // Return mock statistics
        return {
          totalStaff: 5,
          totalCustomers: 150,
          washesToday: 12,
          revenueToday: 1200,
          washesThisWeek: 85,
          revenueThisWeek: 8500,
          washesThisMonth: 320,
          revenueThisMonth: 32000,
          averageRating: 4.5,
          totalSubscriptions: 25,
          activeSubscriptions: 20
        }
      }
      
      // TODO: Implement real statistics calculation
      // This would involve querying multiple collections
      const stats = {
        totalStaff: 0,
        totalCustomers: 0,
        washesToday: 0,
        revenueToday: 0,
        washesThisWeek: 0,
        revenueThisWeek: 0,
        washesThisMonth: 0,
        revenueThisMonth: 0,
        averageRating: 0,
        totalSubscriptions: 0,
        activeSubscriptions: 0
      }
      
      return stats
    } catch (error) {
      console.error('Error fetching branch stats:', error)
      throw new Error('Failed to fetch branch statistics')
    }
  }

  /**
   * Get branch performance metrics
   */
  static async getBranchPerformance(branchId, period = 'month') {
    try {
      if (DEV_MODE) {
        // Return mock performance data
        return {
          washes: {
            total: 320,
            completed: 315,
            cancelled: 5,
            averageTime: 45
          },
          revenue: {
            total: 32000,
            average: 100,
            growth: 12.5
          },
          customers: {
            new: 45,
            returning: 275,
            total: 150
          },
          efficiency: {
            utilization: 85,
            satisfaction: 4.5,
            staffProductivity: 92
          }
        }
      }
      
      // TODO: Implement real performance calculation
      return {
        washes: { total: 0, completed: 0, cancelled: 0, averageTime: 0 },
        revenue: { total: 0, average: 0, growth: 0 },
        customers: { new: 0, returning: 0, total: 0 },
        efficiency: { utilization: 0, satisfaction: 0, staffProductivity: 0 }
      }
    } catch (error) {
      console.error('Error fetching branch performance:', error)
      throw new Error('Failed to fetch branch performance')
    }
  }

  /**
   * Get branch staff
   */
  static async getBranchStaff(branchId) {
    try {
      if (DEV_MODE) {
        return [
          {
            id: 'staff-1',
            name: 'Themba Dlamini',
            role: 'manager',
            email: 'themba@carwashos.co.za',
            phone: '+27821234567',
            isActive: true,
            joinDate: new Date('2024-01-15')
          },
          {
            id: 'staff-2',
            name: 'Nomsa Mthembu',
            role: 'staff',
            email: 'nomsa@carwashos.co.za',
            phone: '+27829876543',
            isActive: true,
            joinDate: new Date('2024-02-01')
          }
        ]
      }
      
      const snapshot = await getDocs(
        query(
          collection(db, 'users'),
          where('branchId', '==', branchId),
          where('isActive', '==', true),
          orderBy('name', 'asc')
        )
      )
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (error) {
      console.error('Error fetching branch staff:', error)
      throw new Error('Failed to fetch branch staff')
    }
  }

  /**
   * Assign manager to branch
   */
  static async assignManager(branchId, managerId) {
    try {
      if (DEV_MODE) {
        const branch = mockBranches.find(b => b.id === branchId)
        if (branch) {
          branch.managerId = managerId
        }
        return true
      }
      
      await runTransaction(db, async (transaction) => {
        // Update branch with new manager
        const branchRef = doc(db, 'branches', branchId)
        transaction.update(branchRef, {
          managerId,
          updatedAt: serverTimestamp()
        })
        
        // Update user role to manager
        const userRef = doc(db, 'users', managerId)
        transaction.update(userRef, {
          role: 'manager',
          branchId,
          updatedAt: serverTimestamp()
        })
      })
      
      return true
    } catch (error) {
      console.error('Error assigning manager:', error)
      throw new Error('Failed to assign manager')
    }
  }

  /**
   * Update branch settings
   */
  static async updateBranchSettings(branchId, settings) {
    try {
      if (DEV_MODE) {
        const branch = mockBranches.find(b => b.id === branchId)
        if (branch) {
          branch.settings = { ...branch.settings, ...settings }
        }
        return true
      }
      
      const docRef = doc(db, 'branches', branchId)
      await updateDoc(docRef, {
        settings,
        updatedAt: serverTimestamp()
      })
      
      return true
    } catch (error) {
      console.error('Error updating branch settings:', error)
      throw new Error('Failed to update branch settings')
    }
  }

  /**
   * Get branch operating hours
   */
  static async getBranchHours(branchId) {
    try {
      if (DEV_MODE) {
        const branch = mockBranches.find(b => b.id === branchId)
        return branch?.settings?.operatingHours || {}
      }
      
      const branch = await this.getBranchById(branchId)
      return branch?.settings?.operatingHours || {}
    } catch (error) {
      console.error('Error fetching branch hours:', error)
      throw new Error('Failed to fetch branch hours')
    }
  }

  /**
   * Check if branch is currently open
   */
  static async isBranchOpen(branchId) {
    try {
      const hours = await this.getBranchHours(branchId)
      const now = new Date()
      const dayOfWeek = now.toLocaleDateString('en-US', { weekday: 'lowercase' })
      const currentTime = now.toLocaleTimeString('en-US', { 
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit' 
      })
      
      const todayHours = hours[dayOfWeek]
      if (!todayHours || !todayHours.isOpen) {
        return false
      }
      
      return currentTime >= todayHours.open && currentTime <= todayHours.close
    } catch (error) {
      console.error('Error checking branch status:', error)
      return false
    }
  }

  /**
   * Get branch services
   */
  static async getBranchServices(branchId) {
    try {
      if (DEV_MODE) {
        const branch = mockBranches.find(b => b.id === branchId)
        return branch?.settings?.services || []
      }
      
      const branch = await this.getBranchById(branchId)
      return branch?.settings?.services || []
    } catch (error) {
      console.error('Error fetching branch services:', error)
      throw new Error('Failed to fetch branch services')
    }
  }

  /**
   * Add service to branch
   */
  static async addBranchService(branchId, service) {
    try {
      if (DEV_MODE) {
        const branch = mockBranches.find(b => b.id === branchId)
        if (branch) {
          branch.settings.services.push({
            id: `service-${Date.now()}`,
            ...service
          })
        }
        return true
      }
      
      const branch = await this.getBranchById(branchId)
      const updatedServices = [...(branch.settings.services || []), {
        id: `service-${Date.now()}`,
        ...service
      }]
      
      await this.updateBranchSettings(branchId, {
        ...branch.settings,
        services: updatedServices
      })
      
      return true
    } catch (error) {
      console.error('Error adding branch service:', error)
      throw new Error('Failed to add branch service')
    }
  }

  /**
   * Update branch service
   */
  static async updateBranchService(branchId, serviceId, serviceData) {
    try {
      if (DEV_MODE) {
        const branch = mockBranches.find(b => b.id === branchId)
        if (branch) {
          const serviceIndex = branch.settings.services.findIndex(s => s.id === serviceId)
          if (serviceIndex !== -1) {
            branch.settings.services[serviceIndex] = {
              ...branch.settings.services[serviceIndex],
              ...serviceData
            }
          }
        }
        return true
      }
      
      const branch = await this.getBranchById(branchId)
      const updatedServices = branch.settings.services.map(service =>
        service.id === serviceId ? { ...service, ...serviceData } : service
      )
      
      await this.updateBranchSettings(branchId, {
        ...branch.settings,
        services: updatedServices
      })
      
      return true
    } catch (error) {
      console.error('Error updating branch service:', error)
      throw new Error('Failed to update branch service')
    }
  }

  /**
   * Delete branch service
   */
  static async deleteBranchService(branchId, serviceId) {
    try {
      if (DEV_MODE) {
        const branch = mockBranches.find(b => b.id === branchId)
        if (branch) {
          branch.settings.services = branch.settings.services.filter(s => s.id !== serviceId)
        }
        return true
      }
      
      const branch = await this.getBranchById(branchId)
      const updatedServices = branch.settings.services.filter(service => service.id !== serviceId)
      
      await this.updateBranchSettings(branchId, {
        ...branch.settings,
        services: updatedServices
      })
      
      return true
    } catch (error) {
      console.error('Error deleting branch service:', error)
      throw new Error('Failed to delete branch service')
    }
  }
}

export const branchService = BranchService 